import * as React from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Card } from './card';
import { searchPayee } from '@mortee/services/payeesServices';
import { transformToUnifiedPayee, UnifiedPayee } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';
import PayeeDetails from '@mortee/components/payeeDetails/PayeeDetails';
import { cn } from '@/lib/utils';

const DEBOUNCE_DELAY_MS = 500;
const MIN_QUERY_LENGTH = 3;
const MAX_SEARCH_RESULTS = 10;

interface BasePayeeSearchInputProps {
  id?: string;
  onPayeeSelected: (payee: UnifiedPayee) => void;
  value?: string;
  onValueChange?: (value: string) => void;
  onManualEditAfterSelection?: () => void;
  placeholder: string;
  className?: string;
  inputClassName?: string;
  getDisplayValueForSelectedPayee: (payee: UnifiedPayee) => string;
  searchCondition?: (query: string) => boolean;
  disabled?: boolean;
}

const BasePayeeSearchInput = observer(
  React.forwardRef<HTMLInputElement, BasePayeeSearchInputProps>(
    (
      {
        id,
        value,
        onValueChange,
        onPayeeSelected,
        onManualEditAfterSelection,
        placeholder,
        className,
        inputClassName,
        getDisplayValueForSelectedPayee,
        searchCondition = (): boolean => true, // Default to always search
        disabled,
      },
      ref,
    ) => {
      const state = useLocalObservable(() => ({
        query: value !== undefined ? value : '',
        results: [] as UnifiedPayee[],
        loading: false,
        open: false,
        debounceTimer: null as NodeJS.Timeout | null,
        internalLastSelectedValue: null as string | null,
        setQuery(val: string): void {
          this.query = val;
        },
        setResults(results: UnifiedPayee[]): void {
          this.results = results;
        },
        setLoading(val: boolean): void {
          this.loading = val;
        },
        setOpen(val: boolean): void {
          this.open = val;
        },
        clearDebounce(): void {
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
          }
        },
        setInternalLastSelectedValue(val: string | null): void {
          this.internalLastSelectedValue = val;
        },
      }));

      React.useEffect(() => {
        if (value !== undefined && state.query !== value) {
          state.setQuery(value);
        }
      }, [value, state]);

      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const val = e.target.value;

        if (state.internalLastSelectedValue !== null && val !== state.internalLastSelectedValue) {
          onManualEditAfterSelection?.();
          state.setInternalLastSelectedValue(null);
        }

        state.setQuery(val);
        onValueChange?.(val);
        state.setOpen(!!val && val.length >= MIN_QUERY_LENGTH);
        state.clearDebounce();
        state.setResults([]);
        if (val.length < MIN_QUERY_LENGTH) {
          state.setLoading(false);
          return;
        }

        // Conditionally skip search
        if (!searchCondition(val)) {
          state.setLoading(false);
          state.setOpen(false);
          return;
        }

        state.setLoading(true);
        state.debounceTimer = setTimeout(async (): Promise<void> => {
          try {
            const res = await searchPayee(val, MAX_SEARCH_RESULTS);
            const mapped = res.map(transformToUnifiedPayee);
            state.setResults(mapped);
          } catch {
            state.setResults([]);
          } finally {
            state.setLoading(false);
          }
        }, DEBOUNCE_DELAY_MS);
      };

      const handleSelect = (payee: UnifiedPayee): void => {
        state.setOpen(false);
        state.setLoading(false);
        state.setResults([]);
        const displayValue = getDisplayValueForSelectedPayee(payee);
        state.setQuery(displayValue);
        state.setInternalLastSelectedValue(displayValue);
        onValueChange?.(displayValue);
        onPayeeSelected(payee);
      };

      const shouldShowPopover = state.open && state.results.length > 0;
      const inputRef = React.useRef<HTMLInputElement>(null);

      // Combine external ref with internal ref using useImperativeHandle
      React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

      return (
        <Popover open={shouldShowPopover} onOpenChange={state.setOpen}>
          <PopoverTrigger asChild>
            <div className={`w-full ${className ?? ''}`}>
              <Input
                ref={inputRef}
                id={id}
                data-testid={id}
                className={cn('w-full', inputClassName)}
                placeholder={placeholder}
                value={state.query}
                onChange={handleInputChange}
                autoComplete='off'
                disabled={disabled}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className='w-[var(--radix-popover-trigger-width)] p-0 mt-2'
            align='start'
            onOpenAutoFocus={(e): void => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            onEscapeKeyDown={(e): void => {
              inputRef.current?.focus();
            }}
          >
            <Card className='p-0 shadow-lg rounded-lg w-full max-h-80 overflow-y-auto'>
              {state.results.map((payee, idx) => (
                <div
                  key={payee.id}
                  className={cn(
                    'cursor-pointer hover:bg-muted px-4 py-3',
                    idx === 0 && 'rounded-t-lg',
                    idx === state.results.length - 1 && 'rounded-b-lg',
                  )}
                  onClick={(): void => handleSelect(payee)}
                >
                  <PayeeDetails payee={payee} searchText={state.query} />
                </div>
              ))}
            </Card>
          </PopoverContent>
        </Popover>
      );
    },
  ),
);

BasePayeeSearchInput.displayName = 'BasePayeeSearchInput';

export default BasePayeeSearchInput;
